/**
 * Marketplace API Client
 * BP-08-04: Marketplace Frontend (Family)
 */

import apiClient from '@/core/lib/api-client';
import type {
  VendorCategory,
  Vendor,
  Product,
  ProductReview,
  Order,
  PaginatedResponse,
  MarketplaceSearchResults,
  FeaturedContent,
  VendorFilters,
  ProductFilters,
  OrderFilters,
  ReviewFilters,
  InquiryRequest,
  QuoteRequest,
  CreateReviewRequest,
} from '@/features/marketplace/types/marketplaceTypes';

// ============================================================================
// Categories
// ============================================================================

export const getCategories = async (): Promise<VendorCategory[]> => {
  const response = await apiClient.get<VendorCategory[]>('/marketplace/categories');
  return response.data;
};

export const getCategory = async (slug: string): Promise<VendorCategory> => {
  const response = await apiClient.get<VendorCategory>(`/marketplace/categories/${slug}`);
  return response.data;
};

// ============================================================================
// Vendors
// ============================================================================

export const getVendors = async (
  filters?: VendorFilters,
  page = 1,
  pageSize = 12
): Promise<PaginatedResponse<Vendor>> => {
  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());
  
  if (filters) {
    if (filters.categoryId) params.set('category_id', filters.categoryId);
    if (filters.search) params.set('search', filters.search);
    if (filters.minRating) params.set('min_rating', filters.minRating.toString());
    if (filters.isVerified !== undefined) params.set('is_verified', filters.isVerified.toString());
    if (filters.serviceState) params.set('service_state', filters.serviceState);
    if (filters.certifications?.length) {
      params.set('certifications', filters.certifications.join(','));
    }
    if (filters.sortBy) params.set('sort_by', filters.sortBy);
  }
  
  const response = await apiClient.get<PaginatedResponse<Vendor>>(
    `/marketplace/vendors?${params.toString()}`
  );
  return response.data;
};

export const getVendor = async (slug: string): Promise<Vendor> => {
  const response = await apiClient.get<Vendor>(`/marketplace/vendors/${slug}`);
  return response.data;
};

export const getVendorProducts = async (
  vendorId: string,
  page = 1,
  pageSize = 12
): Promise<PaginatedResponse<Product>> => {
  const response = await apiClient.get<PaginatedResponse<Product>>(
    `/marketplace/vendors/${vendorId}/products?page=${page}&page_size=${pageSize}`
  );
  return response.data;
};

// ============================================================================
// Products
// ============================================================================

export const getProducts = async (
  filters?: ProductFilters,
  page = 1,
  pageSize = 12
): Promise<PaginatedResponse<Product>> => {
  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());
  
  if (filters) {
    if (filters.categoryId) params.set('category_id', filters.categoryId);
    if (filters.categorySlug) params.set('category_slug', filters.categorySlug);
    if (filters.vendorId) params.set('vendor_id', filters.vendorId);
    if (filters.search) params.set('search', filters.search);
    if (filters.priceMin !== undefined) params.set('price_min', filters.priceMin.toString());
    if (filters.priceMax !== undefined) params.set('price_max', filters.priceMax.toString());
    if (filters.minRating) params.set('min_rating', filters.minRating.toString());
    if (filters.diagnosisIds?.length) {
      params.set('diagnosis_ids', filters.diagnosisIds.join(','));
    }
    if (filters.ageRangeMin !== undefined) params.set('age_range_min', filters.ageRangeMin.toString());
    if (filters.ageRangeMax !== undefined) params.set('age_range_max', filters.ageRangeMax.toString());
    if (filters.acceptsInsurance !== undefined) {
      params.set('accepts_insurance', filters.acceptsInsurance.toString());
    }
    if (filters.isFeatured !== undefined) params.set('is_featured', filters.isFeatured.toString());
    if (filters.sortBy) params.set('sort_by', filters.sortBy);
  }
  
  const response = await apiClient.get<PaginatedResponse<Product>>(
    `/marketplace/products?${params.toString()}`
  );
  return response.data;
};

export const getProduct = async (slug: string): Promise<Product> => {
  const response = await apiClient.get<Product>(`/marketplace/products/${slug}`);
  return response.data;
};

// ============================================================================
// Featured & Search
// ============================================================================

export const getFeatured = async (): Promise<FeaturedContent> => {
  const response = await apiClient.get<FeaturedContent>('/marketplace/featured');
  return response.data;
};

// Backend search response type (different from frontend MarketplaceSearchResults)
interface BackendSearchResult {
  type: 'vendor' | 'product' | 'category';
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  score: number;
  // Vendor-specific fields
  logo_url?: string | null;
  is_verified?: boolean;
  avg_rating?: number | null;
  review_count?: number;
  relevance_score?: number;
  // Product-specific fields
  short_description?: string | null;
  image_url?: string | null;
  price_min?: number | null;
  price_max?: number | null;
  vendor_name?: string | null;
  vendor_slug?: string | null;
  // Category-specific fields
  icon?: string | null;
  product_count?: number;
  parent_id?: string | null;
}

interface BackendSearchResponse {
  query: string | null;
  results: BackendSearchResult[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  facets?: {
    categories: Array<{ id: string; name: string; slug: string; count: number }>;
    price_ranges: Array<{ label: string; min: number; max: number | null }>;
    ratings: Array<{ label: string; min_rating: number }>;
    vendors: unknown[];
    states: unknown[];
  };
  suggestions: string[];
}

export const search = async (
  query: string,
  limit = 10
): Promise<MarketplaceSearchResults> => {
  const response = await apiClient.get<BackendSearchResponse>(
    `/marketplace/search?q=${encodeURIComponent(query)}&page_size=${limit}`
  );

  // Transform backend response to frontend format
  const backendData = response.data;

  const products: Product[] = backendData.results
    .filter((r) => r.type === 'product')
    .map((r) => ({
      id: r.id,
      vendorId: '',
      name: r.name,
      slug: r.slug,
      description: r.description ?? null,
      shortDescription: r.short_description ?? null,
      categoryId: '',
      priceMin: r.price_min ?? null,
      priceMax: r.price_max ?? null,
      priceType: 'fixed' as const,
      acceptsInsurance: false,
      insuranceNotes: null,
      imageUrls: r.image_url ? [r.image_url] : [],
      specifications: [],
      features: [],
      diagnosisIds: [],
      ageRangeMin: null,
      ageRangeMax: null,
      avgRating: r.avg_rating ?? null,
      reviewCount: r.review_count ?? 0,
      status: 'active' as const,
      isFeatured: false,
      createdAt: '',
      updatedAt: '',
      // For display in SearchResultsPage
      vendor_name: r.vendor_name,
      vendor_slug: r.vendor_slug,
    })) as unknown as Product[];

  const vendors: Vendor[] = backendData.results
    .filter((r) => r.type === 'vendor')
    .map((r) => ({
      id: r.id,
      userId: '',
      name: r.name,
      slug: r.slug,
      description: r.description ?? null,
      email: '',
      phone: null,
      website: null,
      address: null,
      serviceAreaNational: false,
      serviceAreaStates: [],
      logoUrl: r.logo_url ?? null,
      bannerUrl: null,
      galleryUrls: [],
      categoryIds: [],
      isVerified: r.is_verified ?? false,
      verifiedAt: null,
      avgRating: r.avg_rating ?? null,
      reviewCount: r.review_count ?? 0,
      yearsInBusiness: null,
      certifications: [],
      insuranceAccepted: [],
      status: 'active' as const,
      featuredUntil: null,
      createdAt: '',
      updatedAt: '',
    })) as Vendor[];

  const categories: VendorCategory[] = backendData.results
    .filter((r) => r.type === 'category')
    .map((r) => ({
      id: r.id,
      slug: r.slug,
      name: r.name,
      description: r.description ?? null,
      icon: r.icon ?? null,
      displayOrder: 0,
      parentId: r.parent_id ?? null,
      productCount: r.product_count ?? 0,
    }));

  return {
    products,
    vendors,
    categories,
    totalProducts: products.length,
    totalVendors: vendors.length,
  };
};

// ============================================================================
// Reviews
// ============================================================================

export const getProductReviews = async (
  productId: string,
  filters?: ReviewFilters,
  page = 1,
  pageSize = 10
): Promise<PaginatedResponse<ProductReview>> => {
  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());
  
  if (filters) {
    if (filters.minRating) params.set('min_rating', filters.minRating.toString());
    if (filters.verifiedOnly) params.set('verified_only', 'true');
    if (filters.withImages) params.set('with_images', 'true');
    if (filters.sortBy) params.set('sort_by', filters.sortBy);
  }
  
  const response = await apiClient.get<PaginatedResponse<ProductReview>>(
    `/products/${productId}/reviews?${params.toString()}`
  );
  return response.data;
};

export const createReview = async (data: CreateReviewRequest): Promise<ProductReview> => {
  const formData = new FormData();
  formData.append('product_id', data.productId);
  formData.append('rating', data.rating.toString());
  formData.append('title', data.title);
  formData.append('content', data.content);
  
  if (data.pros?.length) {
    data.pros.forEach((pro, index) => formData.append(`pros[${index}]`, pro));
  }
  if (data.cons?.length) {
    data.cons.forEach((con, index) => formData.append(`cons[${index}]`, con));
  }
  if (data.images?.length) {
    data.images.forEach((image) => formData.append('images', image));
  }
  
  const response = await apiClient.post<ProductReview>(
    `/products/${data.productId}/reviews`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const markReviewHelpful = async (
  reviewId: string
): Promise<{ helpfulCount: number; isHelpfulByCurrentUser: boolean }> => {
  const response = await apiClient.post<{ helpfulCount: number; isHelpfulByCurrentUser: boolean }>(
    `/reviews/${reviewId}/helpful`
  );
  return response.data;
};

// ============================================================================
// Inquiries & Quotes
// ============================================================================

export const submitInquiry = async (data: InquiryRequest): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    '/marketplace/inquire',
    data
  );
  return response.data;
};

export const requestQuote = async (data: QuoteRequest): Promise<{ success: boolean; message: string; quoteId: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string; quoteId: string }>(
    '/marketplace/quote-request',
    data
  );
  return response.data;
};

// ============================================================================
// Orders
// ============================================================================

export const getOrders = async (
  familyId: string,
  filters?: OrderFilters,
  page = 1,
  pageSize = 10
): Promise<PaginatedResponse<Order>> => {
  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());
  
  if (filters) {
    if (filters.status) params.set('status', filters.status);
    if (filters.vendorId) params.set('vendor_id', filters.vendorId);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    if (filters.sortBy) params.set('sort_by', filters.sortBy);
  }
  
  const response = await apiClient.get<PaginatedResponse<Order>>(
    `/families/${familyId}/orders?${params.toString()}`
  );
  return response.data;
};

export const getOrder = async (orderId: string): Promise<Order> => {
  const response = await apiClient.get<Order>(`/orders/${orderId}`);
  return response.data;
};

// ============================================================================
// Export all functions as namespace
// ============================================================================

export const marketplaceApi = {
  // Categories
  getCategories,
  getCategory,
  
  // Vendors
  getVendors,
  getVendor,
  getVendorProducts,
  
  // Products
  getProducts,
  getProduct,
  
  // Featured & Search
  getFeatured,
  search,
  
  // Reviews
  getProductReviews,
  createReview,
  markReviewHelpful,
  
  // Inquiries & Quotes
  submitInquiry,
  requestQuote,
  
  // Orders
  getOrders,
  getOrder,
};

export default marketplaceApi;

